/**
 * Batch print of account reports ("Print to PDF" of the collective accounts report flow).
 * The flow stores the job in localStorage and opens one tab with the first report; every report page of the job
 * prints itself (one "Save as PDF" dialog per report) and goes on to the next report with a full page load.
 * The report is opened without the `id` of a sent report, so nothing is marked as read for the client.
 */

// The "Print to PDF" button of the collective accounts report flow is frozen (hidden) until a separate task resumes
// the batch print; everything below stays working, `true` brings the button back.
const BATCH_PRINT_ENABLED = false;

type BatchPrintItem = {
  readonly accountId: string;
  readonly email: string;
  readonly name: string;
};

type BatchPrintJob = {
  readonly items: ReadonlyArray<BatchPrintItem>;
  readonly start: string;
  readonly end: string;
  readonly type: 'monthly' | 'yearly';
  readonly lg?: string;
  // index of the report being printed
  readonly index: number;
  // accounts whose report could not be opened
  readonly skipped: ReadonlyArray<string>;
  readonly createdAt: number;
};

const KEY_PREFIX = 'io.reportsBatchPrint.';
// a job left by a closed tab is dropped after a day
const MAX_AGE = 24 * 60 * 60 * 1000;

const readBatchPrint = (key: string): BatchPrintJob | undefined => {
  try {
    const raw = localStorage.getItem(KEY_PREFIX + key);

    return raw ? (JSON.parse(raw) as BatchPrintJob) : undefined;
  } catch {
    return undefined;
  }
};

const saveBatchPrint = (key: string, job: BatchPrintJob): void => {
  try {
    localStorage.setItem(KEY_PREFIX + key, JSON.stringify(job));
  } catch (error) {
    console.error(error);
  }
};

const removeBatchPrint = (key: string): void => {
  try {
    localStorage.removeItem(KEY_PREFIX + key);
  } catch (error) {
    console.error(error);
  }
};

const batchPrintUrl = (key: string, job: BatchPrintJob): string | undefined => {
  const item = job.items[job.index];
  if (!item) return undefined;

  const params = new URLSearchParams({
    start: job.start,
    end: job.end,
    type: job.type,
    email: item.email,
    ...(job.lg ? { lg: job.lg } : {}),
    batch: key,
  });

  return `/management/reports/${item.accountId}?${params.toString()}`;
};

/**
 * Stores a new job and returns the url of its first report.
 */
const createBatchPrint = (job: Omit<BatchPrintJob, 'index' | 'skipped' | 'createdAt'>): string | undefined => {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(KEY_PREFIX))
      .forEach((k) => {
        const stored = readBatchPrint(k.slice(KEY_PREFIX.length));
        (!stored || Date.now() - stored.createdAt > MAX_AGE) && localStorage.removeItem(k);
      });
  } catch (error) {
    console.error(error);
  }

  const key = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  const stored: BatchPrintJob = { ...job, index: 0, skipped: [], createdAt: Date.now() };
  saveBatchPrint(key, stored);

  return readBatchPrint(key) ? batchPrintUrl(key, stored) : undefined;
};

export type { BatchPrintItem, BatchPrintJob };
export { BATCH_PRINT_ENABLED, batchPrintUrl, createBatchPrint, readBatchPrint, removeBatchPrint, saveBatchPrint };
